import { Component, OnDestroy, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { AiService, AiAnalysisRequest } from 'src/app/_services/ai.service';
import { TranslateService } from '@ngx-translate/core';

interface AnalysisOption {
  key: string;
  label: string;
  icon: string;
  description: string;
}

// Parallel to AiAnalysisModalComponent (DLP), for the IRM (labeling/tagging) module.
// Does not modify the DLP modal - it's a separate component with its own endpoint and option set.
@Component({
  selector: 'app-irm-ai-analysis-modal',
  templateUrl: './irm-ai-analysis-modal.component.html',
  styleUrls: ['./irm-ai-analysis-modal.component.css']
})
export class IrmAiAnalysisModalComponent implements OnDestroy {
  @Input() filters: any = {};

  selectedOption: AnalysisOption | null = null;
  isStreaming = false;
  isDone = false;
  streamStatus = '';
  rawMarkdown = '';
  renderedHtml: SafeHtml = '';

  private streamSub?: Subscription;
  private pendingMarkdownChunk = '';
  private renderTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly renderIntervalMs = 80;

  readonly options: AnalysisOption[] = [
    {
      key: 'labeling',
      label: 'aiIrm.option.labeling',
      icon: 'las la-tag',
      description: 'aiIrm.option.labelingDesc'
    },
    {
      key: 'label-changes',
      label: 'aiIrm.option.labelChanges',
      icon: 'las la-edit',
      description: 'aiIrm.option.labelChangesDesc'
    },
    {
      key: 'doc-actions',
      label: 'aiIrm.option.docActions',
      icon: 'las la-file-alt',
      description: 'aiIrm.option.docActionsDesc'
    },
    {
      key: 'label-usage',
      label: 'aiIrm.option.labelUsage',
      icon: 'las la-chart-bar',
      description: 'aiIrm.option.labelUsageDesc'
    }
  ];

  constructor(
    public activeModal: NgbActiveModal,
    private aiService: AiService,
    private sanitizer: DomSanitizer,
    private translate: TranslateService
  ) {}

  selectOption(option: AnalysisOption) {
    this.selectedOption = option;
    this.rawMarkdown = '';
    this.renderedHtml = '';
    this.streamStatus = '';
    this.isDone = false;
    this.runAnalysis();
  }

  runAnalysis() {
    if (!this.selectedOption) return;

    this.isStreaming = true;
    this.isDone = false;
    this.rawMarkdown = '';
    this.renderedHtml = '';
    this.pendingMarkdownChunk = '';
    this.clearRenderTimer();
    this.streamStatus = 'Iniciando analisis...';

    const request: AiAnalysisRequest = {
      analysisType: this.selectedOption.key,
      language: this.translate.currentLang || 'es',
      dateBegin: this.filters?.dateBegin,
      dateEnd: this.filters?.dateEnd,
    };

    this.streamSub = this.aiService.streamIrmAnalysis(request).subscribe({
      next: chunk => {
        if (chunk.startsWith('[STATUS]')) {
          this.streamStatus = chunk.replace('[STATUS]', '').trim();
          return;
        }

        this.enqueueMarkdownChunk(chunk);
      },
      error: err => {
        this.isStreaming = false;
        this.isDone = true;
        this.streamStatus = 'Error en el analisis';
        this.flushPendingMarkdown();
        this.rawMarkdown += `\n\nError: ${err}`;
        this.updateRenderedHtml();
      },
      complete: () => {
        this.isStreaming = false;
        this.isDone = true;
        this.streamStatus = '';
        this.flushPendingMarkdown();
        this.updateRenderedHtml();
      }
    });
  }

  exportPdf() {
    if (!this.rawMarkdown) return;
    const optionLabel = this.translate.instant(this.selectedOption?.label ?? '');
    const cleanedMarkdown = this.rawMarkdown
      .replace(/⏳/g, '')
      .replace(/^\s*\n/gm, '\n')
      .trimStart();
    const content = this.markdownToHtml(cleanedMarkdown);
    const date = new Date().toLocaleDateString('es-ES');

    const printWindow = window.open('', '_blank', 'width=1000,height=700');
    if (!printWindow) return;
    printWindow.document.write(`<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>Análisis IA - IRM - ${optionLabel}</title>
  <style>
    @page { size: A4 landscape; margin: 10mm; }
    body { font-family: Arial, sans-serif; padding: 24px; font-size: 12px; color: #222; }
    h1 { color: #1a237e; font-size: 20px; border-bottom: 2px solid #1a237e; padding-bottom: 8px; margin-bottom: 4px; }
    h2 { color: #283593; font-size: 15px; margin-top: 4px; }
    h3, h4, h5 { color: #3949ab; }
    p { color: #444; line-height: 1.6; }
    .meta { color: #888; font-size: 11px; margin-bottom: 20px; }
    table { width: 100%; border-collapse: collapse; margin: 14px 0; page-break-inside: auto; table-layout: fixed; font-size: 9px; }
    th { background: #1a237e; color: white; padding: 4px 6px; text-align: left; font-weight: 600; border: 1px solid #283593; word-wrap: break-word; overflow-wrap: break-word; }
    td { padding: 4px 6px; border: 1px solid #ddd; vertical-align: top; word-wrap: break-word; overflow-wrap: break-word; }
    tr:nth-child(even) td { background: #f5f7ff; }
    tr { page-break-inside: avoid; }
    @media print { button { display: none; } }
  </style>
</head>
<body>
  <h1>Análisis IA · IRM</h1>
  <h2>${optionLabel}</h2>
  <p class="meta">Generado el ${date} · Safecloud Pro</p>
  ${content}
  <script>window.onload = function() { window.print(); }<\/script>
</body>
</html>`);
    printWindow.document.close();
  }

  goBack() {
    this.streamSub?.unsubscribe();
    this.clearRenderTimer();
    this.selectedOption = null;
    this.isStreaming = false;
    this.isDone = false;
    this.rawMarkdown = '';
    this.renderedHtml = '';
    this.pendingMarkdownChunk = '';
    this.streamStatus = '';
  }

  close() {
    this.streamSub?.unsubscribe();
    this.clearRenderTimer();
    this.activeModal.close();
  }

  ngOnDestroy() {
    this.streamSub?.unsubscribe();
    this.clearRenderTimer();
  }

  private enqueueMarkdownChunk(chunk: string): void {
    if (!chunk) return;

    this.pendingMarkdownChunk += chunk;
    if (this.renderTimer) return;

    this.renderTimer = setTimeout(() => {
      this.flushPendingMarkdown();
      this.updateRenderedHtml();
    }, this.renderIntervalMs);
  }

  private flushPendingMarkdown(): void {
    if (!this.pendingMarkdownChunk) {
      this.clearRenderTimer();
      return;
    }

    this.clearRenderTimer();
    this.rawMarkdown += this.pendingMarkdownChunk;
    this.pendingMarkdownChunk = '';
  }

  private clearRenderTimer(): void {
    if (!this.renderTimer) return;
    clearTimeout(this.renderTimer);
    this.renderTimer = null;
  }

  private updateRenderedHtml(): void {
    this.renderedHtml = this.sanitizer.bypassSecurityTrustHtml(
      this.markdownToHtml(this.rawMarkdown)
    );
  }

  private markdownToHtml(text: string): string {
    if (!text) return '';

    const lines = text.split('\n');
    let html = '';
    let inTable = false;
    let tbodyOpened = false;
    let headerColCount = 0;
    let pendingHeaderCells: string[] | null = null;

    for (const line of lines) {
      const trimmed = line.trim();
      const isTableRow = trimmed.startsWith('|') && trimmed.endsWith('|');
      const isSeparator = isTableRow && /^[\|\-\s:]+$/.test(trimmed);

      if (isTableRow) {
        const cells = trimmed.split('|').filter(c => c.trim() !== '');

        if (!inTable) {
          if (!isSeparator && pendingHeaderCells === null) {
            pendingHeaderCells = cells;
            continue;
          }

          if (isSeparator && pendingHeaderCells !== null) {
            inTable = true;
            headerColCount = pendingHeaderCells.length;
            html += '<table class="ai-table"><thead><tr>';
            for (const c of pendingHeaderCells) html += `<th>${this.processInlineMarkdown(c.trim())}</th>`;
            html += '</tr></thead><tbody>';
            tbodyOpened = true;
            pendingHeaderCells = null;
            continue;
          }

          continue;
        }

        if (isSeparator) {
          continue;
        }

        if (!tbodyOpened) {
          tbodyOpened = true;
          html += '<tbody>';
        }

        const rowCells = cells.slice(0, headerColCount);
        html += '<tr>';
        for (let i = 0; i < headerColCount; i++) {
          const c = (rowCells[i] ?? '').trim();
          html += `<td>${this.processInlineMarkdown(c)}</td>`;
        }
        html += '</tr>';
      } else {
        if (pendingHeaderCells !== null) {
          html += `<p>${this.processInlineMarkdown(pendingHeaderCells.join(' | '))}</p>`;
          pendingHeaderCells = null;
        }

        if (inTable) {
          html += '</tbody></table>';
          inTable = false;
          tbodyOpened = false;
        }
        if (/^[-*_]{3,}$/.test(trimmed)) {
          html += '<hr>';
        } else if (trimmed.startsWith('### ')) {
          html += `<h5>${this.escapeHtml(trimmed.substring(4))}</h5>`;
        } else if (trimmed.startsWith('## ')) {
          html += `<h4>${this.escapeHtml(trimmed.substring(3))}</h4>`;
        } else if (trimmed.startsWith('# ')) {
          html += `<h3>${this.escapeHtml(trimmed.substring(2))}</h3>`;
        } else if (trimmed) {
          html += `<p>${this.processInlineMarkdown(trimmed)}</p>`;
        }
      }
    }

    if (pendingHeaderCells !== null) {
      html += `<p>${this.processInlineMarkdown(pendingHeaderCells.join(' | '))}</p>`;
    }

    if (inTable) html += '</tbody></table>';
    return html;
  }

  private processInlineMarkdown(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>');
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}
